import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useActie } from '../lib/data';
import { toast } from '../ui/toast';
import { Badge, Laden, VerwijderKnop } from '../ui/ui';
import { GoogleLogo } from './Login';

export interface Aanbieders { microsoft: boolean; google: boolean; caldav: boolean }
export interface KoppelingInfo { aanbieder: 'MICROSOFT' | 'GOOGLE' | 'CALDAV'; account: string; status: 'OK' | 'OPNIEUW'; melding: string | null; aangemaakt: string }

export const AANBIEDER_LABEL: Record<KoppelingInfo['aanbieder'], string> = { MICROSOFT: 'Microsoft', GOOGLE: 'Google', CALDAV: 'CalDAV' };

/** Status van de agenda van een engineer (SPEC §8.3). */
export function AgendaStatusBadge({ k, organisatie }: { k: KoppelingInfo | null | undefined; organisatie: boolean }) {
  if (k?.status === 'OPNIEUW') return <Badge toon="rood" title={k.melding ?? undefined}>⚠ Opnieuw koppelen nodig</Badge>;
  if (k) return <Badge toon="groen" title={k.account}>Eigen: {AANBIEDER_LABEL[k.aanbieder]}</Badge>;
  if (organisatie) return <Badge toon="blauw">Organisatie (Microsoft 365)</Badge>;
  return <Badge toon="grijs">Niet gekoppeld (testmodus)</Badge>;
}

const MsLogo = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden>
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

/** Knoppen om een agenda te kiezen; met `uitnodiging` voor de koppellink zonder inloggen. */
export function AgendaKeuze({ aanbieders, uitnodiging }: { aanbieders: Aanbieders; uitnodiging?: string }) {
  const q = uitnodiging ? `?uitnodiging=${encodeURIComponent(uitnodiging)}` : '';
  return (
    <div className="kolom" style={{ gap: 8 }}>
      {aanbieders.microsoft ? (
        <a className="ms-knop" href={`/auth/agenda/microsoft${q}`}>
          <MsLogo /> Microsoft-agenda koppelen
        </a>
      ) : (
        <div className="klein mute">Microsoft is nog niet ingesteld op de server.</div>
      )}
      <div className="klein mute">Werkt met een werk- of schoolaccount én met een privéaccount (Outlook.com, Hotmail, Office 365 Family).</div>
      {aanbieders.google ? (
        <a className="ms-knop" href={`/auth/agenda/google${q}`}>
          <GoogleLogo /> Google-agenda koppelen
        </a>
      ) : (
        <button className="knop" disabled title="Nog niet ingesteld op de server">Google-agenda koppelen · niet beschikbaar</button>
      )}
      {aanbieders.google && <div className="klein mute">Gmail of Google Workspace. Bij een overleg krijgt de afspraak een Google Meet-link.</div>}
      <button className="knop" disabled title="Volgt binnenkort">iCloud / CalDAV koppelen · binnenkort</button>
    </div>
  );
}

interface Mijn { engineer: { naam: string; email: string } | null; koppeling: KoppelingInfo | null; aanbieders: Aanbieders; organisatie: boolean }

/** Mijn agenda: de ingelogde engineer koppelt zijn eigen agenda (SPEC §8.3). */
export function MijnAgenda() {
  const [q, setQ] = useSearchParams();
  const m = useQuery({ queryKey: ['mijn-agenda'], queryFn: () => api.get<Mijn>('/api/agenda/mijn') });
  const ontkoppel = useActie(() => api.post('/api/agenda/mijn/ontkoppelen'), { succes: 'Agenda ontkoppeld', opNa: () => m.refetch() });
  useEffect(() => {
    if (q.get('gekoppeld')) toast('Je agenda is gekoppeld! Je planning wordt nu bijgewerkt.', 'succes');
    if (q.get('fout')) toast(`Koppelen mislukt: ${q.get('fout')}`, 'fout');
    if (q.get('gekoppeld') || q.get('fout')) setQ({}, { replace: true });
  }, [q, setQ]);
  if (!m.data) return <Laden />;
  const d = m.data;
  return (
    <div className="pagina" style={{ maxWidth: 720 }}>
      <div className="paneel kolom">
        <div className="sectie-kop">
          <h1 className="groei">Mijn agenda</h1>
          {d.engineer && <AgendaStatusBadge k={d.koppeling} organisatie={d.organisatie} />}
        </div>
        {!d.engineer ? (
          <p className="mute">Je gebruiker is niet gekoppeld aan een engineer, dus er is geen agenda om te koppelen. Vraag de beheerder om dat bij Gebruikers in te stellen.</p>
        ) : (
          <>
            <p>
              Met een gekoppelde agenda komen je geplande taken, verlof en vrije dagen automatisch in je agenda, en houdt de planning rekening met je eigen afspraken. Er wordt alleen gelezen of je vrij of bezet bent; de inhoud van je eigen afspraken wordt niet opgeslagen.
            </p>
            {d.koppeling && (
              <div className="paneel vlak kolom" style={{ gap: 6 }}>
                <div>
                  Gekoppeld met <b>{AANBIEDER_LABEL[d.koppeling.aanbieder]}</b>: {d.koppeling.account}
                </div>
                {d.koppeling.status === 'OPNIEUW' && <div style={{ color: 'var(--rood)' }}>De koppeling werkt niet meer{d.koppeling.melding ? ` (${d.koppeling.melding})` : ''}. Koppel hieronder opnieuw; wachtende wijzigingen worden daarna alsnog verwerkt.</div>}
                <div className="rij">
                  <VerwijderKnop label="Ontkoppelen" klein onBevestig={() => ontkoppel.mutate()} />
                </div>
              </div>
            )}
            {!d.koppeling && d.organisatie && <div className="paneel vlak">Je organisatie heeft Microsoft 365 al gekoppeld; je agenda doet dus al mee. Een eigen koppeling is alleen nodig als je een andere agenda wilt gebruiken.</div>}
            <h3>{d.koppeling ? 'Opnieuw of met een andere agenda koppelen' : 'Agenda koppelen'}</h3>
            <AgendaKeuze aanbieders={d.aanbieders} />
          </>
        )}
      </div>
    </div>
  );
}
